<template>
  <div>
    <BasePopup :model-value="true" customClass="custom-popup full mn-wd880 history-list">
      <div class="popup-header">
        <h3 class="title">H캐시 거래내역조회</h3>
        <button type="button" class="btn-cancel" @click="handleClose"></button>
      </div>
      <div class="popup-body">
        <div class="box-gray pl32 pr32 search mb40">
          <fieldset class="search-form-wrap">
            <legend>H캐시 거래 내역 검색 폼</legend>
            <div class="search-form">
              <div class="form-box">
                <div class="form-group row">
                  <HconMonthDate label="조회기간" labelClass="mn-wd80" datePickerClass="ml8" :resetPostion="false" v-model:startDate="startDate" v-model:endDate="endDate"/>
                </div>
              </div>
              <div class="form-box">
                <div class="form-group row inline-block w-auto">
                  <HconInput type="search" label="검색어" v-model="inqTitle" placeholder="거래번호/주문번호/거래명을 입력해주세요."  @iputDelete="inqTitle = ''" @searchBtnClick="handleSearch"/>
                </div>
              </div>
            </div>
            <div class="btn-box">
              <button type="button" class="btn lg" @click="handleSearch">조회</button>
            </div>
          </fieldset>
        </div>
        <div class="contents-title-wrap mt40 mb15">
          <div class="contents-title">
            <span class="total font-bold">총 <em class="count text-green">{{ totalData }}</em>건</span>
          </div>
        </div>
        <!-- 스크롤 영역 s-->
        <div class="scroll pb1" :class="hcashHistoryList.length > 0 ? '' : 'h100vh'">
          <table class="tbl-type" :class="hcashHistoryList.length > 0 ? '' : 'h100p'">
            <caption>H캐시 거래 내역 목록</caption>
            <colgroup>
              <col style="width:9%;">
              <col style="width:20%;">
              <col style="width:25%;">
              <col />
            </colgroup>
            <thead :style="hcashHistoryList.length > 0 ? '' : 'height: 39px;'">
              <tr>
                <th scope="col" class="small pl20">선택</th>
                <th scope="col" class="small">거래일시</th>
                <th scope="col" class="small">거래번호/주문번호</th>
                <th scope="col" class="small">거래명/비고</th>
              </tr>
            </thead>
            <tbody v-if="hcashHistoryList && hcashHistoryList.length > 0">
              <tr v-for="(order, index) in hcashHistoryList" :key="index">
                <td class="small pl20">
                  <HconRadioBox id="selectOrder" name="selectOrder" label="H캐시 거래내역 선택" :hide-label="true" :value="order.hcashNum" v-model="selectedHcashNum"/>
                </td>
                <td class="small">{{ HconCommonUtils.format.dateTimeNoSec(order.hcashDate) }}</td>
                <td class="small">{{ order.hcashNum }}</td>
                <td class="small left"><div class="ellips font-bold">{{ order.hcashTitle }}</div></td>
              </tr>
            </tbody>
            <tbody v-else :class="hcashHistoryList.length > 0 ? '' : 'head-100p'">
              <tr>
                <td colspan="4" class="head-100p pt60 pb60">H캐시 거래내역이 없습니다.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- 스크롤 영역 e -->
      </div>
      <div class="popup-footer jc-c">
        <div class="button-box">
          <button type="button" @click="handleSelect" class="btn md">선택 등록</button>
        </div>
      </div>
    </BasePopup>
  </div>
</template>
<script lang="ts" setup>
import { ref } from 'vue';
import HconRadioBox from '@/components/com/HconComPCRadio.vue'
import BasePopup from '@/components/com/HconComBasePopup.vue'
import HconInput from '@/components/com/HconComPCInputField.vue'
import HconMonthDate from '@/components/com/HconComPCDateButton2.vue'
import type { Inquiry } from '@/types/inquiry/backend-dto-types'

// 팝업
const {
  showAlert,
  showConfirm
} = usePopup()

const fetchBuilder  = useHconFetchBuilder()
const inqList = ref<Inquiry>()
const inqTitle = ref('') // 검색어
const startDate = ref('') // 검색 시작일자
const endDate = ref('') // 검색 끝일자
const totalData   = ref<number>(0)  // 총 데이터 건수
const selectedHcashNum = ref('') // 선택된 주문번호
const emit = defineEmits(['close', 'select'])


const searchParams = computed(() => {
  return {
    inqTitle: inqTitle.value,
    startDate: startDate.value,
    endDate: endDate.value,
  };
});

const getHcashHistory = async () => {
  fetchBuilder.get<TableDataInfo<Inquiry>>('/api/v1/inquiry/hcashHistory')
    .query(searchParams.value)
    .progressMessage("잠시만 기다려주세요...")
    .build()
    .execute()
    .then((result) => {
      if(result.resultCode != '200') {
        showAlert(result.resultMessage);
        inqList.value = [];
        totalData.value = 0;
      } else {
        // ref 형식 변수에 값 즉시 세팅
        inqList.value = result.data || [];
        totalData.value = result.total || 0;
      }
  });
}

// 검색
const handleSearch = () => {
  getHcashHistory();
}

// 데이터
interface HcashHistoryData {
  hcashDate: string,
  hcashNum: string,
  hcashTitle: string,
}

const hcashHistoryList = computed<HcashHistoryData[]>(() => {
  const mapped = (inqList.value ?? []).map((item) => {
    return {
      hcashDate: item.orderDtm,
      hcashNum: item.pointApvNo,
      hcashTitle: item.orderNm
    };
  });
  return mapped;
});

onMounted(() => {
  getHcashHistory();
});

const handleClose = () => {
  emit('close');
}

const handleSelect = () => {
  if (!selectedHcashNum.value) {
    // 선택 안했을 때 처리
    showAlert({
      title: '선택 등록 실패',
      message: '거래번호를 먼저 선택해주세요.',
      confirmText: '확인'
    });
    return
  }
  emit('select', selectedHcashNum.value);
}

</script>
<style lang="scss" scoped>
@use "@/assets/scss/pc/cs-hcon.scss" as *;
</style>